import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Mail, CheckCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetLink, setResetLink] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      // In demo mode, the API returns the reset link directly
      if (data.reset_link) setResetLink(data.reset_link);
      toast.success("Demande envoyée!");
    } catch (err) {
      toast.error("Erreur lors de la demande");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md border border-zinc-200 rounded-2xl shadow-sm animate-fade-in" data-testid="forgot-password-card">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 h-12 w-12 rounded-xl bg-emerald-700 flex items-center justify-center">
            {sent ? <CheckCircle className="h-6 w-6 text-white" /> : <Mail className="h-6 w-6 text-white" />}
          </div>
          <CardTitle className="font-heading text-2xl font-bold">
            {sent ? "Courriel envoyé!" : "Mot de passe oublié"}
          </CardTitle>
          <CardDescription>
            {sent
              ? "Verifiez votre boite courriel pour le lien de réinitialisation."
              : "Entrez votre adresse courriel pour recevoir un lien de réinitialisation."
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-4" data-testid="forgot-success">
              <p className="text-sm text-zinc-500 text-center">
                Si un compte existe avec cette adresse, vous recevrez un courriel avec les instructions.
              </p>
              {resetLink && (
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="reset-link-display">
                  <p className="text-xs text-amber-700 mb-2 font-medium">Mode demo - Lien de réinitialisation :</p>
                  <a href={resetLink.replace(process.env.REACT_APP_BACKEND_URL || "", "")} className="text-xs text-emerald-600 underline break-all" data-testid="reset-link">
                    Cliquez ici pour réinitialiser votre mot de passe
                  </a>
                </div>
              )}
              <Button variant="outline" className="w-full rounded-xl" onClick={() => { setSent(false); setEmail(""); setResetLink(""); }} data-testid="try-again-btn">
                Envoyer a une autre adresse
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-form">
              <div>
                <Label htmlFor="email">Adresse courriel</Label>
                <Input
                  id="email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com" required
                  className="mt-1" data-testid="forgot-email-input"
                />
              </div>
              <Button type="submit" className="w-full bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl" disabled={loading} data-testid="forgot-submit-btn">
                {loading ? "Envoi..." : "Envoyer le lien de réinitialisation"}
              </Button>
            </form>
          )}
        </CardContent>
        <CardFooter className="justify-center">
          <Link to="/login" className="text-sm text-emerald-600 hover:underline font-medium flex items-center gap-1" data-testid="back-to-login">
            <ArrowLeft className="h-3 w-3" /> Retour a la connexion
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
}
